import { useEffect, useMemo, useState } from "react";
import "./AdminMusicalIntelligencePreview.css";

const API = process.env.REACT_APP_BACKEND_URL;
const PLACEMENT_LABELS = {
  entrance:  "Entrance",
  offertory: "Offertory",
  communion: "Communion",
  closing:   "Closing / Sending",
};

const TIER_META = {
  1: { label: "Verified", tone: "verified" },
  2: { label: "Canonical", tone: "canonical" },
  3: { label: "Planning", tone: "planning" },
};

function TierBadge({ tier, confidence, verificationLevel }) {
  const meta = TIER_META[tier] || TIER_META[3];
  return (
    <span
      className={`mi-tier mi-tier-${meta.tone}`}
      data-testid={`mi-tier-${meta.tone}`}
      title={verificationLevel || ""}
    >
      T{tier} · {meta.label}
      {typeof confidence === "number" &&
        <span className="mi-conf"> · conf {confidence.toFixed(2)}</span>}
    </span>
  );
}

function ReasoningBlock({ reasoning }) {
  if (!reasoning) return null;
  const cm = reasoning.canonical_metadata || {};
  const tm = reasoning.tune_metadata || {};
  const rs = reasoning.ranking_summary || {};
  const rows = [
    ["Scripture fit",       reasoning.scripture_fit],
    ["Seasonal fit",        reasoning.seasonal_fit],
    ["Placement fit",       reasoning.placement_fit],
    ["Tradition fit",       reasoning.tradition_fit],
    ["Parish resource fit", reasoning.parish_resource_fit],
    ["Rights status",       reasoning.rights_status],
    ["Usage history",       reasoning.usage_history],
  ];
  return (
    <div className="mi-reasoning" data-testid="mi-reasoning">
      <div className="mi-reasoning-grid">
        {rows.map(([label, val]) =>
          val ? (
            <div className="mi-reasoning-row" key={label}>
              <span className="mi-reasoning-label">{label}</span>
              <span className="mi-reasoning-value">{val}</span>
            </div>
          ) : null,
        )}
      </div>
      {(cm.biblical_themes?.length || cm.doctrinal_themes?.length ||
        cm.liturgical_themes?.length || cm.sacramental_associations?.length ||
        cm.feast_suitability?.length) ? (
        <div className="mi-themes">
          {cm.biblical_themes?.length ? (
            <div className="mi-themes-row">
              <span className="mi-themes-label">Biblical themes</span>
              {cm.biblical_themes.map(t => <span key={t} className="mi-chip">{t}</span>)}
            </div>
          ) : null}
          {cm.doctrinal_themes?.length ? (
            <div className="mi-themes-row">
              <span className="mi-themes-label">Doctrinal themes</span>
              {cm.doctrinal_themes.map(t => <span key={t} className="mi-chip">{t}</span>)}
            </div>
          ) : null}
          {cm.liturgical_themes?.length ? (
            <div className="mi-themes-row">
              <span className="mi-themes-label">Liturgical themes</span>
              {cm.liturgical_themes.map(t => <span key={t} className="mi-chip">{t}</span>)}
            </div>
          ) : null}
          {cm.sacramental_associations?.length ? (
            <div className="mi-themes-row">
              <span className="mi-themes-label">Sacramental</span>
              {cm.sacramental_associations.map(t => <span key={t} className="mi-chip">{t}</span>)}
            </div>
          ) : null}
          {cm.feast_suitability?.length ? (
            <div className="mi-themes-row">
              <span className="mi-themes-label">Feast fit</span>
              {cm.feast_suitability.map(t => <span key={t} className="mi-chip">{t}</span>)}
            </div>
          ) : null}
          {(tm.meter || tm.difficulty || tm.instrument_suitability?.length) ? (
            <div className="mi-themes-row">
              <span className="mi-themes-label">Tune</span>
              {tm.meter && <span className="mi-chip">meter: {tm.meter}</span>}
              {tm.difficulty && <span className="mi-chip">difficulty: {tm.difficulty}</span>}
              {tm.instrument_suitability?.map(t => <span key={t} className="mi-chip">{t}</span>)}
            </div>
          ) : null}
        </div>
      ) : null}
      {rs.breakdown ? (
        <details className="mi-breakdown" data-testid="mi-breakdown">
          <summary>Score breakdown — total {rs.total_score}</summary>
          <table>
            <tbody>
              {Object.entries(rs.breakdown).map(([k, v]) => (
                <tr key={k}>
                  <td className="mi-brk-k">{k}</td>
                  <td className="mi-brk-v">{v >= 0 ? `+${v}` : v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}

function FeedbackButtons({ rec, context, onSubmit }) {
  const [pending, setPending] = useState(null);
  const [done, setDone] = useState(null);
  const send = async (action) => {
    if (!rec) return;
    setPending(action);
    try {
      const r = await fetch(`${API}/api/admin/musical-intelligence/feedback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...context,
          canonical_work_id: rec.canonical_work_id,
          resource: rec.resource, number: rec.number,
          action,
        }),
      });
      if (r.ok) { setDone(action); onSubmit && onSubmit(action); }
    } finally {
      setPending(null);
    }
  };
  return (
    <div className="mi-feedback" data-testid="mi-feedback">
      <button
        type="button"
        className={`mi-fb mi-fb-used${done==="used"?" done":""}`}
        disabled={pending !== null}
        onClick={() => send("used")}
        data-testid="mi-fb-used"
      >{pending==="used" ? "…" : done==="used" ? "✓ Used" : "Mark used"}</button>
      <button
        type="button"
        className={`mi-fb mi-fb-not${done==="not_appropriate"?" done":""}`}
        disabled={pending !== null}
        onClick={() => send("not_appropriate")}
        data-testid="mi-fb-not"
      >{pending==="not_appropriate" ? "…" : done==="not_appropriate" ? "✓ Flagged" : "Not appropriate"}</button>
      <button
        type="button"
        className={`mi-fb mi-fb-fav${done==="favorite"?" done":""}`}
        disabled={pending !== null}
        onClick={() => send("favorite")}
        data-testid="mi-fb-fav"
      >{pending==="favorite" ? "…" : done==="favorite" ? "★ Favorited" : "Favorite"}</button>
    </div>
  );
}

function RecommendationCard({ placement, data, context }) {
  const [showWhy, setShowWhy] = useState(false);
  const [showAlt, setShowAlt] = useState(false);
  const rec = data?.recommendation;
  if (!rec) {
    return (
      <div className="mi-card" data-testid={`mi-card-${placement}`}>
        <div className="mi-card-head">
          <h3>{PLACEMENT_LABELS[placement] || placement}</h3>
        </div>
        <div className="mi-empty">No verified or canonical recommendation available for this placement.</div>
      </div>
    );
  }
  return (
    <div className="mi-card" data-testid={`mi-card-${placement}`}>
      <div className="mi-card-head">
        <h3>{PLACEMENT_LABELS[placement] || placement}</h3>
        <TierBadge tier={rec.tier} confidence={rec.confidence}
                   verificationLevel={rec.verification_level} />
      </div>
      <div className="mi-title">{rec.title}</div>
      <div className="mi-meta">
        {rec.tune && <span className="mi-meta-tune">Tune: {rec.tune}</span>}
        {rec.resource && rec.number && (
          <span className="mi-meta-ref">{rec.resource} · #{rec.number}</span>
        )}
        <span className="mi-meta-score">score {rec.score}</span>
      </div>
      <div className="mi-actions">
        <button type="button" className="mi-link" data-testid={`mi-why-${placement}`}
                onClick={() => setShowWhy(v => !v)}>
          {showWhy ? "Hide reasoning" : "Why this hymn?"}
        </button>
        {data.alternatives?.length ? (
          <button type="button" className="mi-link" data-testid={`mi-alt-${placement}`}
                  onClick={() => setShowAlt(v => !v)}>
            {showAlt ? "Hide alternatives" : `Alternatives (${data.alternatives.length})`}
          </button>
        ) : null}
      </div>
      {showWhy && <ReasoningBlock reasoning={rec.reasoning} />}
      {showAlt && data.alternatives?.length ? (
        <div className="mi-alts" data-testid={`mi-alts-${placement}`}>
          {data.alternatives.map(a => (
            <div className="mi-alt" key={a.canonical_work_id}>
              <TierBadge tier={a.tier} confidence={a.confidence}
                         verificationLevel={a.verification_level} />
              <div className="mi-alt-body">
                <div className="mi-alt-title">{a.title}</div>
                <div className="mi-alt-meta">
                  {a.tune && <>Tune: {a.tune} · </>}
                  {a.resource && a.number ? <>{a.resource} #{a.number} · </> : null}
                  score {a.score}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <FeedbackButtons rec={rec} context={{ ...context, placement }} />
    </div>
  );
}

export default function AdminMusicalIntelligencePreview() {
  const [tradition, setTradition]   = useState("episcopal");
  const [occasion, setOccasion]     = useState("First Sunday of Advent");
  const [year, setYear]             = useState("A");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [result, setResult]         = useState(null);
  const [feedbackSummary, setSummary] = useState(null);

  const context = useMemo(
    () => ({ tradition, occasion, year }),
    [tradition, occasion, year],
  );

  const run = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const url = new URL(`${API}/api/admin/musical-intelligence/preview`);
      url.searchParams.set("tradition", tradition);
      url.searchParams.set("occasion", occasion);
      url.searchParams.set("year", year);
      const r = await fetch(url.toString(), { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setResult(j);
    } catch (e) {
      setError(e.message || "Preview failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/admin/musical-intelligence/feedback?limit=1`,
          { credentials: "include" },
        );
        if (r.ok) {
          const j = await r.json();
          setSummary(j.summary);
        }
      } catch {}
    })();
  }, []);

  return (
    <div className="mi-page" data-testid="admin-mi-preview">
      <header className="mi-page-head">
        <h1>Musical Intelligence™ — Recommendation Preview</h1>
        <p className="mi-page-sub">
          Owner-only validation surface for the deterministic
          recommendation engine. No customer traffic. No LLM.
        </p>
        {feedbackSummary && (
          <div className="mi-summary" data-testid="mi-feedback-summary">
            Feedback captured to date —
            <span className="mi-summary-chip">used {feedbackSummary.used || 0}</span>
            <span className="mi-summary-chip">not appropriate {feedbackSummary.not_appropriate || 0}</span>
            <span className="mi-summary-chip">favorite {feedbackSummary.favorite || 0}</span>
          </div>
        )}
      </header>

      <section className="mi-form">
        <label>
          Tradition
          <select
            value={tradition}
            onChange={e => setTradition(e.target.value)}
            data-testid="mi-tradition"
          >
            {["episcopal","roman_catholic","lutheran","methodist",
              "presbyterian","reformed","baptist"].map(t =>
              <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>
          Occasion
          <input
            type="text"
            value={occasion}
            onChange={e => setOccasion(e.target.value)}
            data-testid="mi-occasion"
          />
        </label>
        <label>
          Year
          <select
            value={year}
            onChange={e => setYear(e.target.value)}
            data-testid="mi-year"
          >
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </select>
        </label>
        <button type="button" onClick={run} disabled={loading}
                className="mi-run" data-testid="mi-run">
          {loading ? "Running…" : "Preview recommendations"}
        </button>
      </section>

      {error && <div className="mi-error" data-testid="mi-error">{error}</div>}

      {result?.lectionary?.resolved && (
        <section className="mi-lectionary" data-testid="mi-lectionary">
          <h2>{result.lectionary.display_name}</h2>
          <div className="mi-lect-grid">
            <div><span>OT</span> {result.lectionary.ot.join("; ") || "—"}</div>
            <div><span>Psalm</span> {result.lectionary.psalm.join("; ") || "—"}</div>
            <div><span>Epistle</span> {result.lectionary.epistle.join("; ") || "—"}</div>
            <div><span>Gospel</span> {result.lectionary.gospel.join("; ") || "—"}</div>
          </div>
        </section>
      )}
      {result && !result.lectionary?.resolved && (
        <div className="mi-warn" data-testid="mi-lectionary-unresolved">
          Lectionary could not resolve this occasion; the engine ran without
          scripture. Consider phrasing the occasion as a canonical proper
          (e.g., "First Sunday of Advent").
        </div>
      )}

      {result && (
        <section className="mi-cards">
          {["entrance","offertory","communion","closing"].map(pl => (
            <RecommendationCard
              key={pl}
              placement={pl}
              data={result.recommendations?.[pl]}
              context={context}
            />
          ))}
        </section>
      )}
    </div>
  );
}
